"use client";

import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@pex/ui-web";
import Image from "next/image";

const STAR_KEYS = ["1", "2", "3", "4", "5"] as const;

function StarRating() {
  return (
    <div aria-label="5 out of 5 stars" className="flex gap-0.5" role="img">
      {STAR_KEYS.map((n) => (
        <svg
          aria-hidden="true"
          className="h-5 w-5 fill-amber-400 text-amber-400"
          key={n}
          viewBox="0 0 20 20"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
        </svg>
      ))}
    </div>
  );
}

const TESTIMONIALS = [
  {
    quote:
      "\u201cThe course gives one the courage and confidence in how to handle group facilitation. Thank you for the wealth of knowledge imparted and the opportunity to go and impact our communities!\u201d",
    name: "Simangaliso Ngidi",
    avatar:
      "/images/testimonials/Natural_Wonders_335_Valley_of_Geysers_eivt6l.webp",
  },
  {
    quote:
      "\u201cThis course was a game-changer for me! It also gave me practical tools for setting healthy boundaries, handling challenges, and leading conversations that foster real connection and transformation. If you\u2019re looking to grow in your ability to guide and empower others, this course is definitely worth it!\u201d",
    name: "CJ Philander",
    avatar: "/images/testimonials/Mountains_289_Mountain_in_autumn_3gborc.webp",
  },
  {
    quote:
      "\u201cSo much wisdom and experience was shared, which has already borne fruit in the groups I lead and in my personal life. I am so grateful for the investment you are making into leaders, the local church and the lost.\u201d",
    name: "Andrew",
    avatar:
      "/images/testimonials/Lakes__Rivers___Waterfalls_391_Rhine_river_d5ayah.webp",
  },
  {
    quote:
      "\u201cThis course has been so empowering and has given me the confidence to step out and lead a group. I love the way it is presented; it\u2019s easy to follow and has such valuable information.\u201d",
    name: "Debbie",
    avatar: "/images/testimonials/Historical_Sites_131_Bam_Citadel_nnf10f.webp",
  },
  {
    quote:
      "\u201cThis is a practical, engaging, and meaningful course. Not only are facilitators given techniques, they are challenged and encouraged to grow in their skills and as individuals.\u201d",
    name: "Jonathan Bechtle",
    avatar:
      "/images/testimonials/Countryside_Scenes_235_Pineapple_field_poef7j.webp",
  },
  {
    quote:
      "\u201cA professional course backed by science, scripture and experience. I love the fact that this programme can be used to make a huge difference in individuals\u2019 lives. The self-reflection sections were most helpful in creating self-awareness. Thank you.\u201d",
    name: "Rosemary Giralt",
    avatar: "/images/testimonials/Flowers_462_Fuchsia_pcbmpo.webp",
  },
] as const;

export function SgfTestimonialsCarousel() {
  return (
    <div className="relative">
      {/* Edge fade overlays — pointer-events-none so cards/buttons remain interactive */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute top-0 bottom-16 left-0 z-10 hidden w-32 sm:block"
        style={{
          background:
            "linear-gradient(to right, rgba(255,255,255,0.95) 0%, rgba(255,255,255,0.6) 40%, rgba(255,255,255,0) 100%)",
        }}
      />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute top-0 right-0 bottom-16 z-10 hidden w-32 sm:block"
        style={{
          background:
            "linear-gradient(to left, rgba(255,255,255,0.95) 0%, rgba(255,255,255,0.6) 40%, rgba(255,255,255,0) 100%)",
        }}
      />
      <Carousel className="w-full px-1 pb-4" opts={{ loop: true }}>
        <CarouselContent className="-ml-4">
          {TESTIMONIALS.map((t) => (
            <CarouselItem
              className="pl-4 md:basis-1/2 lg:basis-1/3"
              key={t.name}
            >
              <div className="flex h-full flex-col gap-4 rounded-2xl border bg-card p-6 shadow-sm">
                <StarRating />
                <p className="body-base flex-1">{t.quote}</p>
                <div className="flex items-center gap-3">
                  <div className="relative h-9 w-9 shrink-0 overflow-hidden rounded-full">
                    <Image
                      alt=""
                      aria-hidden="true"
                      className="object-cover"
                      fill
                      sizes="36px"
                      src={t.avatar}
                    />
                  </div>
                  <span className="font-semibold text-sm">{t.name}</span>
                </div>
              </div>
            </CarouselItem>
          ))}
        </CarouselContent>
        <div className="mt-6 flex items-center gap-2 px-1">
          <CarouselPrevious className="static translate-y-0" />
          <CarouselNext className="static translate-y-0" />
        </div>
      </Carousel>
    </div>
  );
}
